/**
 * Guest cart — the bag of someone who hasn't signed in yet.
 *
 * Login is only required at the payment step, so a visitor can craft a formula,
 * add it to their bag and browse the shop with no account at all. That bag lives
 * HERE, in localStorage, and NOT on the server: the cart API is authenticated by
 * design and stays that way. On sign-in the guest bag is replayed into the real
 * cart through the ordinary authenticated endpoints (see mergeGuestCart) and then
 * cleared.
 *
 * SHAPE CONTRACT: stored items match the authenticated cart API's item shape
 * field-for-field (see CartContext.CartItem), so the drawer, the checkout summary
 * and the merge all read one structure. Custom items carry ONE extra field,
 * `custom_payload` — the body POST /api/products/custom needs to create the
 * product row that a guest could not create — and it is dropped on merge.
 */

import type { Cart, CartItem } from '../context/CartContext'

export const GUEST_CART_KEY = 'tpc_guest_cart'

const GATEWAY = process.env.REACT_APP_GATEWAY_URL || 'http://localhost:4000'

// FALLBACK price for a guest bag only, used when GET /api/pricing/formulation
// can't be reached. The real price is admin-editable (Pricing page → app_settings)
// and is fetched at add-to-bag time; this constant only stops a network failure
// from showing ₹0. Provisional and display-only either way: on merge the server
// prices the item itself and its number is the one that is ever charged.
export const CUSTOM_PERFUME_PRICE = 2499

// GST is INCLUSIVE of the listed price everywhere in this platform, so tax is
// extracted from the total, never added on top. Same constants as the server's
// lib/pricing.ts — the guest bag has no server to ask, and the two must agree or
// the totals would visibly change the moment the user signs in.
const TAX_DIVISOR = 1.18
const round2 = (n: number) => Math.round(n * 100) / 100

/** The custom-product creation body, kept alongside a guest custom item. */
export interface GuestCustomPayload {
  formulation_id: string
  name:           string
  mood?:          string
  summary?:       string
  cluster_id?:    number
  cluster?:       string
  notes_top:      string[]
  notes_middle:   string[]
  notes_base:     string[]
}

export interface GuestCartItem extends CartItem {
  custom_payload?: GuestCustomPayload
}

export const EMPTY_CART: Cart = { items: [], total: 0, subtotal: 0, tax: 0, item_count: 0 }

// ── Read / write ──────────────────────────────────────────────────────────────

function readItems(): GuestCartItem[] {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    // Tolerate both the bare array and a full cart object — a stored bag from an
    // older build, or hand-edited storage, must never crash the shop.
    const items = Array.isArray(parsed) ? parsed : parsed?.items
    return Array.isArray(items) ? items.filter(isUsableItem) : []
  } catch {
    return []
  }
}

function isUsableItem(i: any): boolean {
  return !!i && typeof i.id === 'string' && typeof i.product_id === 'string' &&
    Number.isFinite(i.price_at_add) && Number.isFinite(i.quantity) && i.quantity > 0
}

function writeItems(items: GuestCartItem[]): Cart {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify({ items }))
  } catch {
    // Private-browsing / quota errors — the in-memory cart below still works for
    // this page view, which is better than throwing out of an "Add to bag" click.
  }
  return buildCart(items)
}

/** Totals in the same shape (and with the same GST split) the cart API returns. */
function buildCart(items: GuestCartItem[]): Cart {
  const gross    = round2(items.reduce((sum, i) => sum + i.price_at_add * i.quantity, 0))
  const subtotal = round2(gross / TAX_DIVISOR)
  // Derived by subtraction, exactly like the server, so subtotal + tax === total.
  const tax      = round2(gross - subtotal)

  return {
    items,
    total: gross,
    subtotal,
    tax,
    item_count: items.reduce((sum, i) => sum + i.quantity, 0),
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

export function getGuestCart(): Cart {
  return buildCart(readItems())
}

export function hasGuestItems(): boolean {
  return readItems().length > 0
}

/**
 * Add an item, merging quantities with a matching line the same way the server's
 * upsert does (UNIQUE(cart_id, product_type, product_id)).
 */
export function addGuestItem(
  item: Omit<GuestCartItem, 'id' | 'quantity'> & { quantity?: number }
): Cart {
  const items = readItems()
  const quantity = item.quantity ?? 1

  const existing = items.find(
    i => i.product_type === item.product_type && i.product_id === item.product_id
  )
  if (existing) {
    existing.quantity += quantity
  } else {
    items.push({
      ...item,
      quantity,
      // Local line id. Prefixed so it's obvious in a debugger that this row has
      // no server counterpart yet.
      id: `guest_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    })
  }
  return writeItems(items)
}

export function updateGuestQty(itemId: string, quantity: number): Cart {
  const items = readItems()
  if (quantity < 1) return removeGuestItem(itemId)
  const item = items.find(i => i.id === itemId)
  if (item) item.quantity = quantity
  return writeItems(items)
}

export function removeGuestItem(itemId: string): Cart {
  return writeItems(readItems().filter(i => i.id !== itemId))
}

export function clearGuestCart(): Cart {
  try {
    localStorage.removeItem(GUEST_CART_KEY)
  } catch { /* nothing to do */ }
  return EMPTY_CART
}

// ── Merge into the authenticated cart ─────────────────────────────────────────

/**
 * Replay the guest bag into the signed-in user's real cart, then clear it.
 *
 * Each line goes through the ordinary authenticated endpoints — custom items are
 * created as custom products first (POST /api/products/custom is idempotent per
 * formulation_id + user, so a repeat merge can't duplicate them), then added to
 * the cart. A line that can't be added (product deactivated, out of stock) is
 * skipped rather than aborting the whole merge; the customer sees the rest of
 * their bag instead of an error at the worst possible moment.
 *
 * The local bag is cleared even if some lines failed — retrying forever would
 * re-attempt the same doomed lines on every page load.
 *
 * Returns how many lines made it across and how many were skipped.
 */
export async function mergeGuestCart(
  token: string,
  userId: string
): Promise<{ merged: number; skipped: number }> {
  const items = readItems()
  if (items.length === 0) return { merged: 0, skipped: 0 }

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization:  `Bearer ${token}`,
  }

  let merged = 0
  let skipped = 0

  for (const item of items) {
    try {
      let productId = item.product_id

      if (item.product_type === 'custom') {
        if (!item.custom_payload) { skipped++; continue }
        const res = await fetch(`${GATEWAY}/api/products/custom`, {
          method: 'POST',
          headers: authHeaders,
          credentials: 'include',
          body: JSON.stringify({ user_id: userId, ...item.custom_payload }),
        })
        if (!res.ok) { skipped++; continue }
        productId = (await res.json()).id
      }

      const res = await fetch(`${GATEWAY}/api/cart/items`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify({
          product_type: item.product_type,
          product_id:   productId,
          quantity:     item.quantity,
        }),
      })
      if (!res.ok) { skipped++; continue }
      merged++
    } catch {
      skipped++
    }
  }

  clearGuestCart()
  if (skipped > 0) {
    console.warn(`[guest-cart] merged ${merged} item(s), skipped ${skipped} that could not be added`)
  }
  return { merged, skipped }
}
